<template>
    <section class="mobile-product-page">
        <header class="mobile-product-header">
            <span class="mobile-product-header__eyebrow">BIGEMAP PRODUCTS</span>
            <h1>专业 GIS 产品与服务</h1>
            <p>覆盖专业桌面 GIS、移动外业采集与离线地图服务，为不同行业场景提供可靠的空间数据能力。</p>
            <div class="mobile-product-header__tags" aria-label="产品能力">
                <span>专业 GIS</span>
                <span>移动采集</span>
                <span>私有部署</span>
            </div>
        </header>

        <nav class="mobile-product-nav" aria-label="产品分类">
            <button
                v-for="product in products"
                :key="product.id"
                type="button"
                :class="{'mobile-product-nav__item--active': activeProduct === product.id}"
                @click="scrollToProduct(product.id)"
            >
                <strong>{{ product.shortTitle }}</strong>
                <small>{{ product.navSubtitle }}</small>
            </button>
        </nav>

        <main class="mobile-product-main">
            <section
                v-for="(product, productIndex) in products"
                :id="product.id"
                :key="product.id"
                :ref="product.id"
                class="mobile-product-section"
                :class="`mobile-product-section--${product.theme}`"
            >
                <div class="mobile-product-section__heading">
                    <div class="mobile-product-section__number">0{{ productIndex + 1 }}</div>
                    <div>
                        <span>{{ product.eyebrow }}</span>
                        <h2>{{ product.title }}</h2>
                    </div>
                </div>

                <p class="mobile-product-section__summary">{{ product.summary }}</p>

                <div class="mobile-product-section__labels" :aria-label="`${product.title} 适用场景`">
                    <span v-for="label in product.labels" :key="label">{{ label }}</span>
                </div>

                <article v-for="(feature, featureIndex) in product.features" :key="feature.title" class="mobile-product-feature">
                    <div class="mobile-product-feature__image-wrap">
                        <img
                            class="mobile-product-feature__image"
                            :src="feature.image"
                            :alt="feature.alt"
                            loading="lazy"
                        >
                        <span class="mobile-product-feature__index">0{{ featureIndex + 1 }}</span>
                    </div>
                    <div class="mobile-product-feature__body">
                        <span class="mobile-product-feature__kicker">{{ feature.kicker }}</span>
                        <h3>{{ feature.title }}</h3>
                        <p>{{ feature.description }}</p>
                    </div>
                </article>

                <a v-if="product.url" class="mobile-product-section__link" :href="product.url" target="_blank" rel="noopener noreferrer">
                    {{ product.linkText }}
                    <span aria-hidden="true">→</span>
                </a>
            </section>

            <section class="mobile-product-support">
                <span>产品支持</span>
                <h2>使用过程中遇到问题？</h2>
                <p>进入问答社区，查找教程、使用经验和常见问题，也可以向其他用户和技术人员提问。</p>
                <router-link to="/index">进入问答社区</router-link>
            </section>
        </main>
    </section>
</template>

<script>
import proDataManagement from '@/assets/images/products/pro-data-management.webp';
import proSpatialAnalysis from '@/assets/images/products/pro-spatial-analysis.webp';
import proCartography from '@/assets/images/products/pro-cartography.webp';
import appFieldCollection from '@/assets/images/products/app-field-collection.webp';
import appMappingCheckin from '@/assets/images/products/app-mapping-checkin.webp';
import appProSync from '@/assets/images/products/app-pro-sync.webp';
import offlinePrivateDeployment from '@/assets/images/products/offline-private-deployment.webp';
import offlineApiDevelopment from '@/assets/images/products/offline-api-development.webp';
import offline2d3dIntegration from '@/assets/images/products/offline-2d-3d-integration.webp';

export default {
    name: 'MobileProduct',
    data() {
        return {
            activeProduct: 'pro',
            scrollFrame: null,
            products: [
                {
                    id: 'pro',
                    theme: 'blue',
                    shortTitle: 'PRO',
                    navSubtitle: '专业桌面 GIS',
                    eyebrow: 'DESKTOP GIS',
                    title: 'BIGEMAP PRO',
                    summary: '面向专业 GIS 用户的桌面端基础软件，提供从多源数据管理、编辑处理、空间分析到制图输出的完整能力，广泛服务于测绘、规划、自然资源和工程建设等行业。',
                    labels: ['多源数据', '空间分析', '专业制图'],
                    url: 'http://www.bigemap.com/',
                    linkText: '了解 BIGEMAP PRO',
                    features: [
                        {
                            kicker: 'DATA MANAGEMENT',
                            title: '多源数据管理与专业编辑',
                            description: '统一管理卫星影像、航拍数据、矢量数据、栅格数据、CAD 图纸和三维模型，支持数据导入、编辑、清洗、合并、格式转换及坐标投影处理。',
                            image: proDataManagement,
                            alt: 'BIGEMAP PRO 多源空间数据管理界面',
                        },
                        {
                            kicker: 'SPATIAL ANALYSIS',
                            title: '强大的空间分析能力',
                            description: '提供面积距离计算、缓冲区、高程等高线、坡度坡向、流域水系、热力图和地形分析等专业功能，辅助行业分析和项目决策。',
                            image: proSpatialAnalysis,
                            alt: '地形、流域、等高线和热力图空间分析效果',
                        },
                        {
                            kicker: 'MAP PRODUCTION',
                            title: '专业制图与行业成果',
                            description: '支持专题地图设计、地图符号配置、比例尺、指北针、图例和大幅面制图输出，将分析结果转化为可交付、可打印、可存档的专业成果。',
                            image: proCartography,
                            alt: 'BIGEMAP PRO 专业地图制作与打印成果',
                        },
                    ],
                },
                {
                    id: 'app',
                    theme: 'green',
                    shortTitle: 'APP',
                    navSubtitle: '移动外业采集',
                    eyebrow: 'MOBILE FIELDWORK',
                    title: 'BIGEMAP APP',
                    summary: '面向 Android 和 iOS 的专业外业数据采集工具，通过手机完成定位、轨迹记录、点线面标绘、拍照取证和现场打卡，并与 BIGEMAP PRO 协同管理项目数据。',
                    labels: ['数据采集', '标绘拍照', 'PRO 同步'],
                    url: 'http://www.bigemap.com/app/',
                    linkText: '了解 BIGEMAP APP',
                    features: [
                        {
                            kicker: 'FIELD COLLECTION',
                            title: '外业数据采集',
                            description: '基于手机定位能力采集现场位置、行进轨迹和区域范围，适用于野外调查、工程巡检、资源普查和线路采集等工作场景。',
                            image: appFieldCollection,
                            alt: '外业人员使用 BIGEMAP APP 采集轨迹和区域数据',
                        },
                        {
                            kicker: 'MAPPING & CHECK-IN',
                            title: '标绘、拍照与现场打卡',
                            description: '支持点、线、面标绘并自动计算距离和面积，可为采集对象添加属性、现场照片和说明，结合定位打卡完整记录外业工作。',
                            image: appMappingCheckin,
                            alt: '手机地图中的点线面标绘、拍照和定位打卡功能',
                        },
                        {
                            kicker: 'DATA SYNC',
                            title: '与 PRO 数据同步',
                            description: 'APP 采集的图斑、轨迹、照片和属性可以同步到 PRO 进一步整理、分析和制图，PRO 项目数据也可下发到手机端查看、核验和更新。',
                            image: appProSync,
                            alt: 'BIGEMAP APP 与桌面端 PRO 同步空间数据',
                        },
                    ],
                },
                {
                    id: 'offline',
                    theme: 'purple',
                    shortTitle: '离线地图',
                    navSubtitle: '私有部署开发',
                    eyebrow: 'OFFLINE MAP SERVICE',
                    title: 'BIGEMAP 离线地图',
                    summary: '面向政企内网、私有环境和行业项目的离线地图发布与二次开发平台，快速部署本地地图服务，并将二维、三维地图能力集成到业务系统中。',
                    labels: ['离线部署', '开放接口', '二维三维'],
                    // url: 'http://www.bigemap.com/offlinemaps/',
                    url: '',
                    linkText: '了解离线地图服务',
                    features: [
                        {
                            kicker: 'PRIVATE DEPLOYMENT',
                            title: '私有化离线部署',
                            description: '将地图瓦片、影像、高程和业务数据部署到本地服务器，在无互联网或受控网络环境中稳定运行，满足数据安全和自主可控要求。',
                            image: offlinePrivateDeployment,
                            alt: '企业内网中的离线地图服务器私有化部署',
                        },
                        {
                            kicker: 'OPEN API',
                            title: '开放接口与二次开发',
                            description: '提供标准地图服务和开放开发接口，支持 Web、Android、iOS 等平台快速加载离线地图、叠加业务数据并接入现有行业应用。',
                            image: offlineApiDevelopment,
                            alt: '离线地图开放接口连接网页、手机和平板应用',
                        },
                        {
                            kicker: '2D & 3D INTEGRATION',
                            title: '二维三维项目集成',
                            description: '支持二维地图、三维地形、建筑模型、倾斜摄影、热力图和轨迹动画，可用于指挥调度、智慧园区、工程管理和自然资源系统。',
                            image: offline2d3dIntegration,
                            alt: '离线地图二维三维行业系统集成效果',
                        },
                    ],
                },
            ],
        };
    },
    mounted() {
        window.addEventListener('scroll', this.scheduleScrollSpy, {passive: true});
        window.addEventListener('resize', this.scheduleScrollSpy);
        this.$nextTick(this.updateActiveProduct);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.scheduleScrollSpy);
        window.removeEventListener('resize', this.scheduleScrollSpy);
        if (this.scrollFrame !== null) {
            window.cancelAnimationFrame(this.scrollFrame);
        }
    },
    methods: {
        getProductElement(id) {
            const productRef = this.$refs[id];
            return Array.isArray(productRef) ? productRef[0] : productRef;
        },
        scrollToProduct(id) {
            this.activeProduct = id;
            const target = this.getProductElement(id);
            if (!target) {
                return;
            }
            window.scrollTo({
                top: target.offsetTop - 116,
                behavior: 'smooth',
            });
        },
        scheduleScrollSpy() {
            if (this.scrollFrame !== null) {
                return;
            }
            this.scrollFrame = window.requestAnimationFrame(() => {
                this.scrollFrame = null;
                this.updateActiveProduct();
            });
        },
        updateActiveProduct() {
            const activationLine = 128;
            let currentProduct = this.products[0].id;

            this.products.forEach(product => {
                const target = this.getProductElement(product.id);
                if (target && target.getBoundingClientRect().top <= activationLine) {
                    currentProduct = product.id;
                }
            });

            this.activeProduct = currentProduct;
        },
    },
};
</script>

<style scoped lang="less">
.mobile-product-page {
    min-height: 100vh;
    color: #172033;
    background: #f3f6fa;
}

.mobile-product-header {
    position: relative;
    overflow: hidden;
    padding: 42px 22px 38px;
    color: #fff;
    background:
        radial-gradient(circle at 90% 16%, rgba(60, 143, 255, 0.42), transparent 32%),
        radial-gradient(circle at 12% 100%, rgba(38, 203, 180, 0.2), transparent 42%),
        linear-gradient(145deg, #07152f 0%, #0b2c5f 58%, #124890 100%);
}

.mobile-product-header::after {
    position: absolute;
    right: -64px;
    bottom: -86px;
    width: 220px;
    height: 220px;
    content: '';
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    box-shadow: 0 0 0 38px rgba(255, 255, 255, 0.035), 0 0 0 76px rgba(255, 255, 255, 0.025);
}

.mobile-product-header__eyebrow {
    position: relative;
    z-index: 1;
    display: block;
    margin-bottom: 12px;
    color: #7fc2ff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.8px;
}

.mobile-product-header h1 {
    position: relative;
    z-index: 1;
    margin: 0;
    font-size: 27px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.5px;
}

.mobile-product-header p {
    position: relative;
    z-index: 1;
    max-width: 340px;
    margin: 13px 0 22px;
    color: rgba(255, 255, 255, 0.74);
    font-size: 13px;
    line-height: 1.75;
}

.mobile-product-header__tags {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 8px;
}

.mobile-product-header__tags span {
    padding: 6px 10px;
    color: rgba(255, 255, 255, 0.86);
    font-size: 11px;
    line-height: 1;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 999px;
}

.mobile-product-nav {
    position: sticky;
    z-index: 20;
    top: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.96);
    border-bottom: 1px solid #e8edf4;
    box-shadow: 0 5px 20px rgba(31, 45, 61, 0.06);
    backdrop-filter: blur(12px);
}

.mobile-product-nav button {
    padding: 8px 4px 7px;
    color: #788397;
    font-family: inherit;
    text-align: center;
    background: transparent;
    border: 0;
    border-radius: 10px;
    outline: none;
    -webkit-tap-highlight-color: transparent;
}

.mobile-product-nav strong,
.mobile-product-nav small {
    display: block;
}

.mobile-product-nav strong {
    margin-bottom: 3px;
    font-size: 13px;
    font-weight: 650;
}

.mobile-product-nav small {
    font-size: 9px;
    opacity: 0.72;
}

.mobile-product-nav__item--active {
    color: #176de5 !important;
    background: #edf5ff !important;
}

.mobile-product-main {
    padding: 18px 14px 26px;
}

.mobile-product-section {
    margin-bottom: 18px;
    padding: 22px 14px 18px;
    background: #fff;
    border: 1px solid #e9eef5;
    border-radius: 18px;
    box-shadow: 0 8px 30px rgba(23, 32, 51, 0.06);
    scroll-margin-top: 112px;
}

.mobile-product-section__heading {
    display: flex;
    align-items: center;
    gap: 12px;
}

.mobile-product-section__number {
    display: flex;
    flex: 0 0 43px;
    width: 43px;
    height: 43px;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    border-radius: 13px;
}

.mobile-product-section__heading span,
.mobile-product-feature__kicker,
.mobile-product-support > span {
    display: block;
    margin-bottom: 3px;
    color: #8b96a9;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.1px;
}

.mobile-product-section__heading h2 {
    margin: 0;
    color: #172033;
    font-size: 21px;
    font-weight: 700;
    line-height: 1.25;
}

.mobile-product-section__summary {
    margin: 15px 0 13px;
    color: #657085;
    font-size: 13px;
    line-height: 1.75;
}

.mobile-product-section__labels {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    margin-bottom: 18px;
}

.mobile-product-section__labels span {
    padding: 5px 9px;
    font-size: 10px;
    border-radius: 6px;
}

.mobile-product-feature {
    overflow: hidden;
    margin-bottom: 14px;
    background: #fff;
    border: 1px solid #e9eef5;
    border-radius: 14px;
}

.mobile-product-feature__image-wrap {
    position: relative;
    overflow: hidden;
    background: #dce5f1;
}

.mobile-product-feature__image {
    display: block;
    width: 100%;
    aspect-ratio: 8 / 5;
    object-fit: cover;
}

.mobile-product-feature__index {
    position: absolute;
    top: 10px;
    left: 10px;
    display: flex;
    width: 29px;
    height: 29px;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    background: rgba(4, 18, 42, 0.74);
    border: 1px solid rgba(255, 255, 255, 0.26);
    border-radius: 9px;
    backdrop-filter: blur(7px);
}

.mobile-product-feature__body {
    padding: 14px 14px 15px;
}

.mobile-product-feature__body h3 {
    margin: 0 0 8px;
    color: #1d283b;
    font-size: 16px;
    font-weight: 650;
    line-height: 1.35;
}

.mobile-product-feature__body p {
    margin: 0;
    color: #6c7689;
    font-size: 12px;
    line-height: 1.75;
}

.mobile-product-section__link {
    display: flex;
    height: 42px;
    margin-top: 4px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    border-radius: 11px;
}

.mobile-product-section__link:hover,
.mobile-product-section__link:focus {
    color: #fff;
    text-decoration: none;
}

.mobile-product-section--blue .mobile-product-section__number,
.mobile-product-section--blue .mobile-product-section__link {
    background: linear-gradient(135deg, #176de5, #3b91fa);
}

.mobile-product-section--blue .mobile-product-section__labels span {
    color: #176de5;
    background: #edf5ff;
}

.mobile-product-section--green .mobile-product-section__number,
.mobile-product-section--green .mobile-product-section__link {
    background: linear-gradient(135deg, #078a73, #27b99a);
}

.mobile-product-section--green .mobile-product-section__labels span {
    color: #078a73;
    background: #eaf9f5;
}

.mobile-product-section--purple .mobile-product-section__number,
.mobile-product-section--purple .mobile-product-section__link {
    background: linear-gradient(135deg, #6454ce, #8a73e8);
}

.mobile-product-section--purple .mobile-product-section__labels span {
    color: #6654cf;
    background: #f1efff;
}

.mobile-product-support {
    padding: 26px 20px;
    color: #fff;
    text-align: center;
    background: linear-gradient(145deg, #111d35, #1e3d6d);
    border-radius: 18px;
}

.mobile-product-support > span {
    color: #79baff;
}

.mobile-product-support h2 {
    margin: 5px 0 9px;
    font-size: 20px;
}

.mobile-product-support p {
    margin: 0 0 18px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    line-height: 1.7;
}

.mobile-product-support a {
    display: inline-flex;
    height: 38px;
    padding: 0 18px;
    align-items: center;
    justify-content: center;
    color: #176de5;
    font-size: 12px;
    font-weight: 600;
    text-decoration: none;
    background: #fff;
    border-radius: 999px;
}

@media (min-width: 600px) {
    .mobile-product-main {
        max-width: 620px;
        margin: 0 auto;
    }

    .mobile-product-header {
        padding-right: calc((100% - 576px) / 2);
        padding-left: calc((100% - 576px) / 2);
    }

    .mobile-product-nav {
        max-width: 620px;
        margin: 0 auto;
    }
}
</style>
